import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { ScanField } from "@/components/ScanField";
import { LotPicker } from "@/components/LotPicker";
import { Button } from "@/components/ui/button";

import { looksLikeLotOrCrate } from "@/lib/scan";
import { useFlash } from "@/floor/useFlash";
import { ArrowLeft, PackagePlus, CheckCircle2, MapPin, Loader2, Package, ChevronRight } from "lucide-react";

// Floor-native Tag Parts: the FLOOR task is "work an OPEN lot and rapid-scan tags into it".
// Creating a lot / bonding a donor truck / merging lots is setup → stays a desk task.
//
// BATCH ONE (his instruction 2026-10-07):
//  A — SCAN THE CRATE, SKIP THE LIST. A crate or lot label opens its lot, so the 900-row list is
//      no longer the only way in. Scanning a DIFFERENT crate while tagging switches lots with a
//      loud flash naming the new one, so it can never happen silently.
//  F — FINISHED WITH THIS LOT. Say where the crate went and the lot closes. "Close without
//      moving" is a second, deliberate tap and the SERVER refuses it while the crate is
//      Unplaced — "no closing a lot whose crate nobody can find."
export default function FloorTagParts() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [lot, setLot] = useState(null);
  const [count, setCount] = useState(0);
  const [last, setLast] = useState("");
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [binOpen, setBinOpen] = useState(false);
  const [binParts, setBinParts] = useState(null);
  const { trigger, FlashOverlay } = useFlash();

  // THE LOT IS IN THE ADDRESS BAR, so walking into a part and coming back lands you right back on
  // this lot (his instruction, 2026-10-14: *"going BACK from a part returns to that same lot
  // screen, right where I was."*)
  const pickLot = useCallback((l, { push = true } = {}) => {
    setLot(l); setCount(l.total_count || 0); setLast(""); setFinishing(false);
    setBinOpen(false); setBinParts(null);
    if (push) setSp(l?.id ? { lot: l.id } : {}, { replace: false });
  }, [setSp]);

  const lotParam = sp.get("lot");
  useEffect(() => {
    if (!lotParam) { setLot(null); return; }
    if (lot && lot.id === lotParam) return;
    api.get(`/capture-batches/${lotParam}`)
      .then(({ data }) => pickLot({ ...data.batch, id: data.batch.id || lotParam }, { push: false }))
      .catch(() => setSp({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotParam]);

  // THE PARTS STILL IN THE BIN — tap the count, see them, tap one to go to it.
  const openBin = async () => {
    setBinOpen((o) => !o);
    if (binParts) return;
    try {
      const { data } = await api.get(`/capture-batches/${lot.id}`);
      const crate = data.batch?.container_id;
      setBinParts((data.parts || []).filter((p) => String(p.location?.id || "") === String(crate || "")));
    } catch (e) { setBinParts([]); }
  };

  const openByScan = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get("/capture-batches/by-scan", { params: { code } });
      pickLot(data.lot);
      trigger("success", `${data.lot.batch_number} — ${data.lot.total_count || 0} parts`);
    } catch (e) { trigger("error", apiErrorMessage(e) || "NOT A LOT"); }
    finally { setBusy(false); }
  };

  // ONE BOX, TWO JOBS. A crate/lot label switches lot; anything else is a tag for this lot.
  const onScan = async (value, meta) => {
    if (looksLikeLotOrCrate(value)) return openByScan(value);
    setBusy(true);
    try {
      const { data } = await api.post(`/capture-batches/${lot.id}/capture`, { tag_code: value,
                                                                              provenance: prov("floor_tag_parts", meta) });
      setCount(data.batch_total ?? (count + 1)); setLast(data.part?.short_code || "");
      // a part tagged into the lot lands in the lot's crate, so the bin count goes up with it
      setLot((prev) => (prev ? { ...prev, in_crate: (prev.in_crate ?? 0) + 1 } : prev));
      trigger("success", `TAGGED · ${data.part?.short_code || ""}`.trim());
    } catch (e) { trigger("error", apiErrorMessage(e) || "TAG FAILED"); }
    finally { setBusy(false); }
  };

  const finish = async (payload, words) => {
    setClosing(true);
    try {
      const { data } = await api.post(`/capture-batches/${lot.id}/finish`, payload);
      trigger("success", `${data.batch_number} CLOSED`);
      setLot(null); setFinishing(false); setSp({});
      setLast(""); setCount(0);
      return data;
    } catch (e) { trigger("error", apiErrorMessage(e) || words || "COULD NOT CLOSE"); }
    finally { setClosing(false); }
  };

  const placed = !!lot?.current_location?.placed;
  const inBin = lot?.in_crate ?? 0;      // parts still sitting in this lot's own crate

  return (
    <div className="flex-1 flex flex-col p-4 sm:p-6 gap-5" data-testid="floor-tag-parts">
      {FlashOverlay}
      <button onClick={() => (lot ? setSp({}) : navigate("/floor"))} className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start" data-testid="floor-back">
        <ArrowLeft className="w-5 h-5" /> {lot ? "Lots" : "Home"}
      </button>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl bg-[#B01E28] flex items-center justify-center"><PackagePlus className="w-8 h-8 text-white" /></div>
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight leading-none">Tag Parts</h1>
          <p className="text-base text-slate-500 font-medium mt-1">{lot ? "Scan tags into this lot." : "Scan a crate, or pick a lot."}</p>
        </div>
      </div>

      {!lot ? (
        <div className="flex flex-col gap-5" data-testid="floor-lot-list">
          {/* A — THE WAY IN THAT ISN'T A LIST */}
          <div className="rounded-xl border-2 border-[#E0781A] bg-[#FFF9F3] p-4" data-testid="floor-lot-scan-card">
            <ScanField testid="floor-lot-scan" label="Scan the crate — or the lot label"
              placeholder="Scan a crate / lot label, or type LOT-00123"
              submitLabel="Open it" onScan={openByScan} busy={busy} />
          </div>
          <LotPicker variant="floor" onPick={pickLot} />
        </div>
      ) : finishing ? (
        <div className="flex flex-col gap-4" data-testid="floor-finish-panel">
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
            <div className="font-display text-2xl font-black text-emerald-900 leading-tight">Finished with {lot.batch_number}?</div>
            <p className="text-base text-emerald-800 mt-1">
              Scan where the crate went, and the lot closes.
            </p>
            <p className="text-sm text-emerald-700 mt-2">Closing means <span className="font-bold">done moving</span>, not gone — the lot stays searchable with its full history, and every part keeps its link back to it.</p>
          </div>
          {/* HIS RULE, 2026-10-14: a lot is finished when its BIN IS EMPTY — every part scanned out
              onto a shelf. The server refuses either way; this says it before he scans anything,
              with the number still in the bin and the way to clear them. */}
          {inBin > 0 ? (
            <>
              <div data-testid="floor-finish-bin-not-empty"
                className="rounded-xl border-2 border-[#B01E28] bg-[#FDF3F4] p-4">
                <div className="font-display text-2xl font-black text-[#B01E28] leading-tight">
                  {inBin} part{inBin === 1 ? "" : "s"} still in this bin
                </div>
                <p className="text-base text-slate-700 mt-1">
                  Scan {inBin === 1 ? "it" : "them"} onto a shelf first. A lot is finished when its
                  bin is empty — identifying can carry on afterwards.
                </p>
              </div>
              <Button data-testid="floor-finish-putaway" onClick={() => navigate("/floor/putaway")}
                className="h-16 rounded-xl text-lg font-black bg-[#B01E28] hover:bg-[#8f1820] text-white">
                <MapPin className="w-6 h-6 mr-2" />Put parts away
              </Button>
            </>
          ) : (
          <>
          <ScanField testid="floor-finish-dest" label="Scan where the crate went"
            placeholder="Scan a bin / shelf / row label" submitLabel="Move it & close"
            onScan={(code) => finish({ destination_scan: code })} busy={closing} />
          {placed ? (
            <Button data-testid="floor-finish-no-move" variant="outline" disabled={closing}
              onClick={() => finish({ close_without_moving: true })}
              className="h-14 rounded-xl text-base font-bold border-2 border-slate-300">
              {closing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <MapPin className="w-5 h-5 mr-2" />}
              It's already home — close without moving
            </Button>
          ) : count === 0 ? (
            // HIS EXCEPTION, 2026-10-08. The must-scan rule below protects a crate with parts in
            // it. An empty lot has nothing inside to lose, so it only blocks tidying up lots made
            // by accident. The SERVER still decides — it refuses if the lot is under a week old or
            // ever held a part — so this button cannot talk its way past the rule.
            <Button data-testid="floor-finish-empty" variant="outline" disabled={closing}
              onClick={() => finish({ close_without_moving: true, reason: "empty — never used" })}
              className="h-14 rounded-xl text-base font-bold border-2 border-slate-300">
              {closing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <MapPin className="w-5 h-5 mr-2" />}
              Nothing was ever put in it — close as empty
            </Button>
          ) : (
            <div data-testid="floor-finish-must-scan" className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-base font-semibold text-amber-900">
              This crate has no location recorded, so you must scan where it went — nobody could find it otherwise.
            </div>
          )}
          </>
          )}
          <Button data-testid="floor-finish-cancel" variant="ghost" onClick={() => setFinishing(false)}
            className="h-12 text-base font-bold text-slate-500">Not yet — keep tagging</Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border-2 border-[#E2E8F0] bg-white p-4 flex items-center justify-between" data-testid="floor-tag-lot">
            <div className="min-w-0">
              <div className="font-mono font-bold text-lg truncate">{lot.batch_number}</div>
              <div className="text-base text-slate-500 truncate">{lot.donor_label || lot.donor?.stock_number || (lot.kind === "legacy" ? "Legacy lot" : "No truck")}</div>
              <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5" data-testid="floor-tag-crate">
                <MapPin className={`w-4 h-4 ${placed ? "text-[#C76714]" : "text-slate-300"}`} />
                {lot.current_location?.label || "Crate not placed yet"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-black" data-testid="floor-tag-count">{count}</div>
              <div className="text-xs uppercase tracking-wider text-slate-400">tagged</div>
              {inBin > 0 ? (
                <button type="button" data-testid="floor-tag-in-bin" onClick={openBin}
                  className="mt-1 font-bold text-[#C76714] text-base underline decoration-dotted underline-offset-4">
                  {inBin} in bin
                </button>
              ) : count > 0 ? (
                <div data-testid="floor-tag-bin-empty" className="mt-1 font-bold text-emerald-700 text-base">bin empty</div>
              ) : null}
            </div>
          </div>
          {/* WHAT IS STILL IN THE BIN — tapped open from the count, each part tappable through to
              itself. Coming back lands on this same lot, because the lot is in the address bar. */}
          {binOpen && (
            <div data-testid="floor-bin-list" className="rounded-xl border-2 border-[#E0781A] bg-[#FFF9F3] overflow-hidden">
              <div className="px-4 py-2.5 font-display text-xl font-black text-[#C76714]">
                Still in this bin{binParts ? ` — ${binParts.length}` : ""}
              </div>
              {binParts === null ? (
                <div className="px-4 pb-4 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" />Loading…</div>
              ) : binParts.length === 0 ? (
                <div data-testid="floor-bin-list-empty" className="px-4 pb-4 text-base text-slate-600">Nothing left in the bin.</div>
              ) : binParts.map((p) => (
                <button key={p.id} type="button" data-testid={`floor-bin-part-${p.short_code}`}
                  onClick={() => navigate(`/floor/part/${p.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-t border-[#E0781A]/30 text-left active:bg-[#FFEFE0]">
                  <Package className="w-6 h-6 text-[#C76714] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-bold text-lg truncate">{p.short_code}</div>
                    <div className="text-sm text-slate-500 truncate">{p.name || "Not identified yet"}</div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
          {last && <div className="text-base font-bold text-emerald-700" data-testid="floor-tag-last">Last: {last}</div>}
          <div data-testid="floor-tag-scan">
            <ScanField testid="floor-tag-scan" label="Scan a FAS tag — or a crate to switch lots"
              submitLabel="Tag it" onScan={onScan} busy={busy} continuous />
          </div>
          <Button data-testid="floor-finish-lot" onClick={() => setFinishing(true)}
            className="h-16 rounded-xl text-lg font-black bg-emerald-700 hover:bg-emerald-800 text-white">
            <CheckCircle2 className="w-6 h-6 mr-2" />Finished with this lot
          </Button>
        </div>
      )}
    </div>
  );
}
